/**
 * Actions abstraites du jeu.
 * Liste provisoire : la liste définitive dépend du gameplay, non tranché
 * (voir docs/open-questions.md).
 */
export type ActionName = "primary" | "secondary" | "jump";

export const ACTION_NAMES: readonly ActionName[] = [
  "primary",
  "secondary",
  "jump",
];

export interface Axis2 {
  x: number;
  y: number;
}

/**
 * Contrat unique entre les périphériques et le reste du jeu.
 * Toutes les valeurs sont dans [-1, 1] et indépendantes du framerate :
 * `look` et `zoom` sont des vitesses (unités/seconde normalisées), pas des
 * deltas bruts, donc un consommateur les multiplie par dt.
 */
export interface InputState {
  /** Direction voulue. y > 0 = avant. */
  move: Axis2;
  /** Vitesse de visée. x > 0 = vers la droite, y > 0 = vers le haut. */
  look: Axis2;
  /** Vitesse de zoom. > 0 = rapprocher. */
  zoom: number;
  actions: Record<ActionName, boolean>;
  /** Position du pointeur en coordonnées normalisées [-1, 1] ([TD-041]). */
  pointer: Axis2;
  /** Un pointeur survole-t-il la scène ? Toujours faux au tactile ([GD-007]). */
  pointerPresent: boolean;
  /**
   * Le zoom a-t-il un point de convergence ? Vrai sous le curseur, vrai au
   * milieu d'un pincement, faux sinon — le zoom vise alors le centre de
   * l'écran ([GD-043]). Distinct de `pointerPresent` : un pincement a une
   * position sans rien survoler.
   */
  zoomAnchored: boolean;
  /** Vrai pendant la seule image où un clic ou un appui s'est achevé. */
  pick: boolean;
}

/** Vue non mutable de l'état, seule forme exposée hors de core/input/. */
export interface ReadonlyInputState {
  readonly move: Readonly<Axis2>;
  readonly look: Readonly<Axis2>;
  readonly zoom: number;
  readonly actions: Readonly<Record<ActionName, boolean>>;
  readonly pointer: Readonly<Axis2>;
  readonly pointerPresent: boolean;
  readonly zoomAnchored: boolean;
  readonly pick: boolean;
}

/**
 * Accumulateur brut rempli par les sources pendant une frame.
 * `lookDelta` et `zoomDelta` sont en pixels CSS : la conversion en vitesse
 * normalisée est faite une seule fois, par InputManager.
 */
export interface InputSample {
  move: Axis2;
  /**
   * Déplacement en pixels, pour les sources qui glissent au lieu de pousser :
   * un doigt sur la carte ([GD-045]). Converti en direction par
   * `InputManager`, comme `lookDelta` et `zoomDelta`.
   */
  moveDelta: Axis2;
  lookDelta: Axis2;
  zoomDelta: number;
  actions: Record<ActionName, boolean>;
  /** Déjà normalisée : seule la source connaît la taille de sa cible. */
  pointer: Axis2;
  pointerPresent: boolean;
  zoomAnchored: boolean;
  pick: boolean;
}

/**
 * Un périphérique. Ajouter une manette = un fichier de plus qui implémente
 * cette interface, zéro modification ailleurs.
 */
export interface InputSource {
  readonly name: string;
  /**
   * Ajoute sa contribution dans `out` et remet ses accumulateurs à zéro.
   * `dt` permet à une source d'étaler un événement discret dans le temps.
   */
  drain(out: InputSample, dt: number): void;
  dispose(): void;
}
